import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Head, Loader, EmptyBox, L, STAGES } from "./shared";
import { LifecycleTabs } from "@/components/lifecycle/LifecycleTabs";
import { ArchivedPanel } from "@/components/lifecycle/ArchivedPanel";
import { GripVertical, Clock, Calendar, X, Send, CircleDollarSign, Rocket, Check, Trash2, Archive, Plus, Loader2, Palette, ExternalLink, Globe, MessageSquare } from "lucide-react";

const STAGE_PHASE = {
  "New Inquiry": ["Inquiry", "text-blue-300", "border-blue-400/40", "bg-blue-500/10"],
  "Discovery": ["Inquiry", "text-blue-300", "border-blue-400/40", "bg-blue-500/10"],
  "Proposal Sent": ["Proposal", "text-gold", "border-gold/40", "bg-gold/10"],
  "Proposal Accepted": ["Proposal", "text-gold", "border-gold/40", "bg-gold/10"],
  "Design": ["Design", "text-purple-300", "border-purple-400/40", "bg-purple-500/10"],
  "Revision": ["Design", "text-purple-300", "border-purple-400/40", "bg-purple-500/10"],
  "Development": ["Development", "text-cyan-300", "border-cyan-400/40", "bg-cyan-500/10"],
  "Testing": ["Development", "text-cyan-300", "border-cyan-400/40", "bg-cyan-500/10"],
  "Launch": ["Launch", "text-green-300", "border-green-400/40", "bg-green-500/10"],
  "Completed": ["Launch", "text-green-300", "border-green-400/40", "bg-green-500/10"],
};

// Due-date status for pipeline cards (skips launched/completed projects)
function dueStatus(dueDate, stage) {
  if (!dueDate || stage === "Launch" || stage === "Completed") return null;
  const due = new Date(dueDate);
  if (isNaN(due)) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((due - today) / 86400000);
  if (diffDays < 0) return { label: `Overdue ${Math.abs(diffDays)}d`, cls: "text-red-300 bg-red-500/15 border-red-500/30" };
  if (diffDays <= 3) return { label: diffDays === 0 ? "Due today" : `Due in ${diffDays}d`, cls: "text-gold bg-gold/15 border-gold/30" };
  return null;
}

export function Projects() {
  const [projects, setProjects] = useState(null);
  const [drag, setDrag] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [focusStage, setFocusStage] = useState(null);
  const [phaseFilter, setPhaseFilter] = useState("Total");
  const [lifeTab, setLifeTab] = useState("active");
  const boardRef = useRef(null);
  const load = () => api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    try {
      const fs = sessionStorage.getItem("admin_focus_stage");
      if (fs) {
        setFocusStage(fs);
        setPhaseFilter((STAGE_PHASE[fs] || ["Total"])[0]);
        sessionStorage.removeItem("admin_focus_stage");
        setTimeout(() => {
          document.querySelector(`[data-testid="pipeline-column-${fs.replace(/\s+/g, "-").toLowerCase()}"]`)?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
        }, 300);
        setTimeout(() => setFocusStage(null), 4000);
      }
    } catch (e) {}
  }, []);

  const selectPhase = (ph) => {
    setPhaseFilter(ph);
    setTimeout(() => boardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const move = async (id, stage) => {
    setProjects((ps) => ps.map((p) => (p.id === id ? { ...p, stage } : p)));
    try { await api.put(`/projects/${id}/status`, { status: stage }); } catch (e) { toast.error(formatApiError(e)); load(); }
  };

  if (!projects) return <Loader />;
  const openProject = projects.find((p) => p.id === openId);
  const phaseCounts = {};
  projects.forEach((p) => { const ph = (STAGE_PHASE[p.stage] || ["Other"])[0]; phaseCounts[ph] = (phaseCounts[ph] || 0) + 1; });

  const weekAgo = Date.now() - 7 * 86400000;
  const newThisWeek = projects.filter((p) => p.created_at && new Date(p.created_at).getTime() >= weekAgo).length;
  const phaseTrend = (ph) => {
    if (ph === "Total") return newThisWeek > 0 ? { text: `+${newThisWeek} this week`, cls: "text-emerald-400" } : { text: "All clear", cls: "text-gray-500" };
    const ps = projects.filter((p) => (STAGE_PHASE[p.stage] || [])[0] === ph);
    let overdue = 0, dueToday = 0;
    ps.forEach((p) => { const d = dueStatus(p.due_date, p.stage); if (d) { if (d.label.startsWith("Overdue")) overdue++; else if (d.label === "Due today") dueToday++; } });
    if (overdue > 0) return { text: `${overdue} overdue`, cls: "text-red-300" };
    if (dueToday > 0) return { text: `${dueToday} due today`, cls: "text-gold" };
    return { text: "All clear", cls: "text-gray-500" };
  };
  const KPIS = [
    { ph: "Total", count: projects.length },
    ...["Inquiry", "Proposal", "Design", "Development", "Launch"].map((ph) => ({ ph, count: phaseCounts[ph] || 0 })),
  ];
  const visibleStages = phaseFilter === "Total" ? STAGES : STAGES.filter((s) => (STAGE_PHASE[s] || [])[0] === phaseFilter);

  return (
    <div>
      <Head title="Project Pipeline" desc="Click a stage to filter — drag projects from inquiry to launch." />
      <div className="mb-6"><LifecycleTabs module="projects" value={lifeTab} onChange={setLifeTab} refreshKey={projects.length} /></div>
      {lifeTab !== "active" ? <ArchivedPanel module="projects" status={lifeTab} singular="Project" onChanged={load} /> : (<>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="pipeline-metrics">
        {KPIS.map((k) => {
          const active = phaseFilter === k.ph;
          const tr = phaseTrend(k.ph);
          return (
            <button key={k.ph} onClick={() => selectPhase(k.ph)} data-testid={`kpi-${k.ph.toLowerCase()}`} aria-pressed={active}
              className={`text-left px-4 py-3 border transition-all duration-200 cursor-pointer ${active
                ? "bg-gold/10 border-gold shadow-lg shadow-gold/10 -translate-y-0.5"
                : "bg-navy-800 border-white/10 hover:border-gold/50 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/30"}`}>
              <div className="flex items-center justify-between">
                <span className={`text-xs uppercase tracking-wider ${active ? "text-gold" : "text-gray-400"}`}>{k.ph}</span>
                {active && <span className="w-1.5 h-1.5 rounded-full bg-gold" />}
              </div>
              <div className="text-white text-2xl font-heading leading-tight mt-1">{k.count}</div>
              <div className={`text-[11px] mt-0.5 ${tr.cls}`} data-testid={`kpi-trend-${k.ph.toLowerCase()}`}>{tr.text}</div>
            </button>
          );
        })}
      </div>
      {phaseFilter !== "Total" && (
        <div className="flex items-center gap-2 mb-4 text-sm text-gray-400" data-testid="pipeline-filter-banner">
          <span>Showing <span className="text-gold font-medium">{phaseFilter}</span> stage{visibleStages.length > 1 ? "s" : ""}</span>
          <button onClick={() => setPhaseFilter("Total")} data-testid="pipeline-filter-clear" className="inline-flex items-center gap-1 text-gray-400 hover:text-white border border-white/15 hover:border-gold/50 px-2 py-0.5 transition-colors"><X size={12} /> Show all</button>
        </div>
      )}
      {projects.length === 0 ? <EmptyBox text="No projects yet." /> : (
        <div ref={boardRef} className="flex gap-4 overflow-x-auto pb-4 scroll-mt-24" data-testid="pipeline-board">
          {visibleStages.map((stage) => {
            const inStage = projects.filter((p) => p.stage === stage);
            const [, txt, bord] = STAGE_PHASE[stage] || ["", "text-gold", "border-gold/40"];
            return (
              <div key={stage} className={`w-64 shrink-0 rounded transition-all ${focusStage === stage ? "ring-2 ring-gold bg-gold/5 p-2 -m-2" : ""}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { if (drag && drag.stage !== stage) move(drag.id, stage); setDrag(null); }}
                data-testid={`pipeline-column-${stage.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className={`text-xs uppercase tracking-wider ${txt} mb-3 flex items-center justify-between border-l-2 ${bord} pl-2`}>
                  <span>{stage}</span><span className="text-gray-500">{inStage.length}</span>
                </div>
                <div className="space-y-3 min-h-[80px]">
                  {inStage.map((p) => {
                    const prog = Math.round(((STAGES.indexOf(p.stage) + 1) / STAGES.length) * 100);
                    const due = dueStatus(p.due_date, p.stage);
                    return (
                      <div key={p.id} draggable onDragStart={() => setDrag(p)} onDragEnd={() => setDrag(null)}
                        onClick={() => setOpenId(p.id)}
                        className="bg-navy-800 border border-white/10 p-4 cursor-pointer hover:border-gold/40 transition-colors" data-testid={`pipeline-card-${p.id}`}>
                        <div className="flex items-start gap-2">
                          <GripVertical size={14} className="text-gray-600 mt-0.5 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="text-white font-medium text-sm mb-0.5 truncate">{p.org_name}</div>
                            <div className="text-gray-500 text-xs mb-2 truncate">{p.package_name || "Custom"}</div>
                          </div>
                        </div>
                        {due && (
                          <div className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 border mb-2 ${due.cls}`} data-testid={`due-badge-${p.id}`}>
                            <Clock size={10} /> {due.label}
                          </div>
                        )}
                        <div className="h-1 bg-white/10 mb-2"><div className="h-1 bg-gold" style={{ width: `${prog}%` }} /></div>
                        <div className="flex items-center justify-between text-[11px] text-gray-500">
                          {p.due_date ? <span className="flex items-center gap-1"><Calendar size={11} /> {new Date(p.due_date).toLocaleDateString()}</span> : <span>{prog}%</span>}
                          {p.assignee && <span className="w-5 h-5 rounded-full bg-gold/20 border border-gold/40 flex items-center justify-center text-gold text-[9px] font-bold">{p.assignee[0]?.toUpperCase()}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
      </>)}
      {openProject && <ProjectDrawer project={openProject} onClose={() => setOpenId(null)} onChange={load} />}
    </div>
  );
}

function ProjectDrawer({ project, onClose, onChange }) {
  const [p, setP] = useState(project);
  const [events, setEvents] = useState([]);
  const [newTask, setNewTask] = useState("");
  const [concept, setConcept] = useState(null);
  useEffect(() => { api.get(`/projects/${project.id}/events`).then((r) => setEvents(r.data)).catch(() => {}); }, [project.id]);
  useEffect(() => { api.get(`/admin/projects/${project.id}/design-concept`).then((r) => setConcept(r.data && r.data.html ? r.data : null)).catch(() => {}); }, [project.id]);
  const openConcept = () => {
    if (!concept?.html) return;
    const w = window.open("", "_blank");
    if (w) { w.document.open(); w.document.write(concept.html); w.document.close(); }
  };

  const patch = async (payload) => {
    setP((x) => ({ ...x, ...payload }));
    try { await api.patch(`/projects/${project.id}/meta`, payload); onChange?.(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const tasks = p.tasks || [];
  const addTask = () => { if (!newTask.trim()) return; patch({ tasks: [...tasks, { id: Date.now().toString(), text: newTask.trim(), done: false }] }); setNewTask(""); };
  const toggleTask = (id) => patch({ tasks: tasks.map((t) => (t.id === id ? { ...t, done: !t.done } : t)) });
  const delTask = (id) => patch({ tasks: tasks.filter((t) => t.id !== id) });
  const done = tasks.filter((t) => t.done).length;

  const setStage = async (stage) => {
    setP((x) => ({ ...x, stage }));
    try { await api.put(`/projects/${project.id}/status`, { status: stage }); onChange?.(); toast.success(`Moved to ${stage}`); } catch (e) { toast.error(formatApiError(e)); }
  };
  const archiveProject = async () => {
    if (!window.confirm(`Archive "${p.org_name}"? History, files, messages & invoices are preserved. Restore or permanently delete it from the Records Center.`)) return;
    try { await api.post(`/lifecycle/projects/${project.id}/archive`, { reason: "" }); toast.success("Project archived."); onChange?.(); onClose(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const [showInvoice, setShowInvoice] = useState(false);
  const [invoiceAmount, setInvoiceAmount] = useState("");
  const [invoiceDesc, setInvoiceDesc] = useState("Project invoice");
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const createInvoice = async () => {
    const amt = Number(invoiceAmount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount."); return; }
    setCreatingInvoice(true);
    try {
      await api.post(`/projects/${project.id}/invoices`, { amount: amt, kind: "balance", description: invoiceDesc.trim() || "Project invoice" });
      toast.success("Invoice created");
      setShowInvoice(false); setInvoiceAmount(""); setInvoiceDesc("Project invoice");
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreatingInvoice(false); }
  };
  const [sendingProposal, setSendingProposal] = useState(false);
  const sendProposal = async () => {
    if (!project.request_id) { toast.error("No questionnaire on file for this project."); return; }
    setSendingProposal(true);
    try {
      await api.post(`/proposals/from-request/${project.request_id}`);
      setP((x) => ({ ...x, stage: "Proposal Sent" }));
      toast.success("Proposal generated & sent — client can view it in their portal.");
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSendingProposal(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex justify-end z-50" onClick={onClose}>
      <div className="bg-navy-800 border-l border-white/10 w-full max-w-md h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="project-drawer">
        <div className="flex justify-between items-start mb-1">
          <div><h3 className="font-heading text-2xl text-white">{p.org_name}</h3><p className="text-gray-500 text-sm">{p.package_name || "Custom project"}</p></div>
          <button onClick={onClose} aria-label="Close"><X className="text-gray-400 hover:text-white" /></button>
        </div>
        <span className={`inline-block text-xs px-2 py-0.5 mb-5 border ${(STAGE_PHASE[p.stage] || ["", "text-gold", "border-gold/40"])[2]} ${(STAGE_PHASE[p.stage] || ["", "text-gold"])[1]}`}>{p.stage}</span>

        {/* Quick actions */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="drawer-actions">
          <button onClick={sendProposal} disabled={sendingProposal} data-testid="action-send-proposal" className="flex items-center gap-1.5 text-sm bg-gold text-navy px-3 py-1.5 font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">{sendingProposal ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Send Proposal</button>
          <button onClick={() => setShowInvoice(true)} data-testid="action-invoice" className="flex items-center gap-1.5 text-sm border border-gold/40 text-gold px-3 py-1.5 hover:bg-gold/10 transition-colors"><CircleDollarSign size={14} /> Generate Invoice</button>
          <button onClick={() => setStage("Launch")} data-testid="action-launch" className="flex items-center gap-1.5 text-sm border border-green-400/40 text-green-300 px-3 py-1.5 hover:bg-green-500/10 transition-colors"><Rocket size={14} /> Mark Launched</button>
          <button onClick={archiveProject} data-testid="action-archive-project" className="flex items-center gap-1.5 text-sm border border-red-500/30 text-red-300 px-3 py-1.5 hover:bg-red-500/10 transition-colors"><Archive size={14} /> Archive</button>
        </div>

        {/* Client-selected design concept */}
        {concept && (
          <div className="bg-gold/5 border border-gold/25 p-4 mb-6" data-testid="drawer-design-concept">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-sm text-white"><Palette size={15} className="text-gold" /> Client's chosen homepage concept <span className="text-gray-500">· variation {(concept.variation || 0) + 1}</span></div>
              <button onClick={openConcept} data-testid="drawer-open-concept" className="inline-flex items-center gap-1.5 text-xs bg-gold text-navy px-3 py-1.5 font-semibold hover:bg-gold-light transition-colors"><ExternalLink size={13} /> Open concept</button>
            </div>
            <p className="text-xs text-gray-400 mt-2">Use this as the design starting point for the build.</p>
          </div>
        )}

        {/* Starting site — from a converted website preview */}
        {p.site_starter && (
          <div className="bg-emerald-500/5 border border-emerald-500/25 p-4 mb-6" data-testid="drawer-site-starter">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-sm text-white"><Globe size={15} className="text-emerald-300" /> Starting site <span className="text-gray-500">· {p.site_starter.block_count} content blocks</span></div>
              <a href={p.site_starter.preview_url} target="_blank" rel="noopener noreferrer" data-testid="drawer-open-starter" className="inline-flex items-center gap-1.5 text-xs bg-emerald-500/90 text-navy px-3 py-1.5 font-semibold hover:bg-emerald-400 transition-colors"><ExternalLink size={13} /> Open preview</a>
            </div>
            <p className="text-xs text-gray-400 mt-2">The generated website content carried over from the preview — the starting point for this build.</p>
          </div>
        )}

        {/* Latest client feedback on a shared preview */}
        {p.latest_feedback && (
          <div className="bg-navy border border-white/10 p-4 mb-6" data-testid="drawer-preview-feedback">
            <div className="flex items-center gap-2 text-sm text-white mb-1">
              <MessageSquare size={15} className="text-gold" /> Client preview feedback
              <span className={`text-xs px-2 py-0.5 rounded-full border ${p.latest_feedback.sentiment === "love" ? "border-emerald-500/40 text-emerald-300" : "border-amber-400/40 text-amber-300"}`}>
                {p.latest_feedback.sentiment === "love" ? "Loved it" : "Wants changes"}
              </span>
            </div>
            {p.latest_feedback.message && <p className="text-gray-300 text-sm mt-1">“{p.latest_feedback.message}”</p>}
            <p className="text-gray-600 text-xs mt-1">{[p.latest_feedback.name, p.latest_feedback.email].filter(Boolean).join(" · ")}</p>
          </div>
        )}

        {/* Details */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <L label="Due date"><input type="date" value={p.due_date ? p.due_date.slice(0, 10) : ""} onChange={(e) => patch({ due_date: e.target.value })} data-testid="drawer-due-date" className="w-full bg-navy border border-white/20 text-white p-2 text-sm focus:border-gold focus:outline-none" /></L>
          <L label="Assignee"><input value={p.assignee || ""} onChange={(e) => setP((x) => ({ ...x, assignee: e.target.value }))} onBlur={(e) => patch({ assignee: e.target.value })} placeholder="Team member" data-testid="drawer-assignee" className="w-full bg-navy border border-white/20 text-white p-2 text-sm focus:border-gold focus:outline-none" /></L>
        </div>

        {/* Checklist */}
        <div className="mb-6" data-testid="drawer-checklist">
          <div className="flex items-center justify-between mb-2"><span className="text-sm text-white font-medium">Tasks</span><span className="text-xs text-gray-500">{done}/{tasks.length}</span></div>
          <div className="space-y-1.5 mb-3">
            {tasks.map((t) => (
              <div key={t.id} className="flex items-center gap-2 group">
                <button onClick={() => toggleTask(t.id)} data-testid={`task-toggle-${t.id}`} className={`w-4 h-4 border flex items-center justify-center shrink-0 ${t.done ? "bg-gold border-gold" : "border-white/30"}`}>{t.done && <Check size={11} className="text-navy" />}</button>
                <span className={`text-sm flex-1 ${t.done ? "text-gray-500 line-through" : "text-gray-200"}`}>{t.text}</span>
                <button onClick={() => delTask(t.id)} className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-400"><Trash2 size={12} /></button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input value={newTask} onChange={(e) => setNewTask(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTask()} placeholder="Add a task…" data-testid="drawer-task-input" className="flex-1 bg-navy border border-white/20 text-white p-2 text-sm focus:border-gold focus:outline-none" />
            <button onClick={addTask} data-testid="drawer-task-add" className="bg-gold text-navy px-3 hover:bg-gold-light transition-colors"><Plus size={16} /></button>
          </div>
        </div>

        {/* Activity timeline */}
        <div data-testid="drawer-timeline">
          <span className="text-sm text-white font-medium">Activity</span>
          <div className="mt-3 space-y-3 border-l border-white/10 pl-4">
            {events.length === 0 ? <p className="text-gray-500 text-sm">No activity yet.</p> : events.map((ev) => (
              <div key={ev.id} className="relative">
                <span className="absolute -left-[21px] top-1.5 w-2 h-2 rounded-full bg-gold" />
                <p className="text-gray-200 text-sm">{ev.text}</p>
                <p className="text-gray-600 text-xs">{new Date(ev.created_at).toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showInvoice && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4" onClick={() => setShowInvoice(false)} data-testid="invoice-modal">
          <div className="bg-navy border border-white/15 w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1"><h4 className="font-heading text-lg text-white">New invoice</h4><button onClick={() => setShowInvoice(false)} aria-label="Close"><X size={18} className="text-gray-400 hover:text-white" /></button></div>
            <p className="text-xs text-gray-500 mb-4">Tip: check this project's existing invoices first so you don't bill twice.</p>
            <label className="block text-sm text-gray-400 mb-1">Amount ($)</label>
            <input type="number" min="0" step="0.01" value={invoiceAmount} onChange={(e) => setInvoiceAmount(e.target.value)} autoFocus data-testid="invoice-amount-input"
              className="w-full bg-navy-800 border border-white/20 text-white p-2.5 text-sm focus:border-gold focus:outline-none mb-3" placeholder="e.g. 1247.50" />
            <label className="block text-sm text-gray-400 mb-1">Description</label>
            <input value={invoiceDesc} onChange={(e) => setInvoiceDesc(e.target.value)} data-testid="invoice-desc-input"
              className="w-full bg-navy-800 border border-white/20 text-white p-2.5 text-sm focus:border-gold focus:outline-none mb-5" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowInvoice(false)} data-testid="invoice-cancel" className="px-4 py-2 text-sm border border-white/20 text-gray-300 hover:bg-white/5 transition-colors">Cancel</button>
              <button onClick={createInvoice} disabled={creatingInvoice} data-testid="invoice-create" className="px-4 py-2 text-sm bg-gold text-navy font-semibold hover:bg-gold-light transition-colors disabled:opacity-50 flex items-center gap-1.5">{creatingInvoice ? <Loader2 className="animate-spin" size={14} /> : <CircleDollarSign size={14} />} Create invoice</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
